import { useTranslation, type TranslatedString } from "@i18n";
import { GROUPING_WINDOW_MS, EDIT_WINDOW_MS } from "../portalChat.constants";
import type { ChatMessage, ChatConversation, ChatMember } from "../api/portalChat.api";
import { isSameDay } from "../components/context";

/**
 * Pure thread-rendering rules shared by the web pane and the mobile screen. Everything here is a
 * function of message/thread data — no state, no DOM — so the two renderers cannot drift on the
 * PRD behaviours (grouping window, edit window, read ticks, system-message wording).
 *
 * `@i18n` resolves to each platform's own typed i18n module, so `ChatTranslate` is the platform's
 * key-checked `t` — every key used here must exist in en.json on both platforms (it's one file).
 */
export type ChatTranslate = ReturnType<typeof useTranslation>["t"];

/** PRD: own text messages are editable for 15 minutes; tombstones and system lines never. */
export function canEditMessage(message: ChatMessage, selfId: string): boolean {
  return (
    String(message.senderId) === selfId &&
    !message.deleted &&
    message.kind === "text" &&
    Date.now() - message.createdDate <= EDIT_WINDOW_MS
  );
}

/** min(lastReadDate) across every OTHER member — a message is "read by everyone" up to this. */
export function everyoneReadUpTo(
  members: ChatMember[],
  memberReadDates: { userId: string; lastReadDate: number }[],
  selfId: string,
): number {
  const others = members.filter((member) => String(member._id) !== selfId);
  if (!others.length) return 0;
  let minRead = Number.MAX_SAFE_INTEGER;
  for (const member of others) {
    const read = memberReadDates.find((r) => r.userId === String(member._id))?.lastReadDate ?? 0;
    minRead = Math.min(minRead, read);
  }
  return minRead === Number.MAX_SAFE_INTEGER ? 0 : minRead;
}

/** How many OTHER members' read cursors cover this message — the "✓✓ N" receipt chip on own bubbles. */
export function readerCountFor(
  message: ChatMessage,
  members: ChatMember[],
  memberReadDates: { userId: string; lastReadDate: number }[],
  selfId: string,
): number {
  let count = 0;
  for (const member of members) {
    if (String(member._id) === selfId) continue;
    const read = memberReadDates.find((r) => r.userId === String(member._id))?.lastReadDate ?? 0;
    if (read >= message.createdDate) count += 1;
  }
  return count;
}

/** Tick state for an own message: null (not own / deleted), "sent" (✓ gray), "read" (✓✓ blue). */
export function tickStateFor(message: ChatMessage, selfId: string, readUpTo: number): "sent" | "read" | null {
  if (String(message.senderId) !== selfId || message.deleted) return null;
  return readUpTo >= message.createdDate ? "read" : "sent";
}

/** PRD §grouping: same sender, same day, previous not system, within 60s → compact bubble. */
export function isGroupedWithPrevious(message: ChatMessage, previous: ChatMessage | undefined): boolean {
  if (!previous) return false;
  return (
    isSameDay(previous.createdDate, message.createdDate) &&
    previous.kind !== "system" &&
    String(previous.senderId) === String(message.senderId) &&
    message.createdDate - previous.createdDate <= GROUPING_WINDOW_MS
  );
}

/** A day separator renders above the first message of each calendar day. */
export function showDaySeparator(message: ChatMessage, previous: ChatMessage | undefined): boolean {
  return !previous || !isSameDay(previous.createdDate, message.createdDate);
}

/**
 * PRD §4.3 system-line wording matrix: renamed / memberAdded / memberRemoved, worded per audience
 * (the actor, the target, everyone else). Falls back to the stored text for unknown events.
 */
export function systemLineText(message: ChatMessage, selfId: string, t: ChatTranslate): string {
  const system = message.system;
  if (!system) return message.text ?? "";
  const actorIsSelf = String(system.actorId) === selfId;
  const actorName = actorIsSelf ? t("You") : system.actorName;
  if (system.event === "renamed") {
    return t("{{actor}} renamed the chat to {{name}}", { actor: actorName, name: system.toName ?? "" });
  }
  const targets = system.targets ?? [];
  const selfIsTarget = targets.some((target) => String(target._id) === selfId);
  const names = targets
    .filter((target) => String(target._id) !== selfId)
    .map((target) => target.name)
    .join(", ");
  if (system.event === "memberAdded") {
    if (selfIsTarget) {
      return t("You were added to this chat by {{actor}}. You can see everything posted before you joined.", {
        actor: system.actorName,
      });
    }
    if (actorIsSelf) {
      return t("{{names}} was added to this chat by you. They can see everything posted before they joined.", {
        names,
      });
    }
    return t("{{actor}} added {{names}} to this chat. They can see everything posted before they joined.", {
      actor: system.actorName,
      names,
    });
  }
  if (selfIsTarget) {
    return t("You were removed from this chat by {{actor}}.", { actor: system.actorName });
  }
  return t("{{names}} was removed from this chat by {{actor}}. They no longer have access to this chat.", {
    names,
    actor: actorIsSelf ? t("you") : system.actorName,
  });
}

/** The "X is typing…" line: 1 name / two names / a count. Null when nobody is typing. */
export function typingLineText(users: { userName: string }[], t: ChatTranslate): TranslatedString | null {
  const [first, second, ...more] = users;
  if (!first) return null;
  if (!second) return t("{{name}} is typing…", { name: first.userName });
  if (!more.length) return t("{{a}} and {{b}} are typing…", { a: first.userName, b: second.userName });
  return t("{{count}} people are typing…", { count: users.length });
}

/** Thread-header subtitle: space → "Space chat · project", group → "N members · topic". */
export function conversationSubtitle(conversation: ChatConversation, t: ChatTranslate): string {
  if (conversation.type === "space") {
    return `${t("Space chat")}${conversation.projectName ? ` · ${conversation.projectName}` : ""}`;
  }
  if (conversation.type === "group") {
    return `${conversation.memberCount} ${t("members")}${conversation.topic ? ` · ${conversation.topic}` : ""}`;
  }
  return "";
}

/** Attachment size for cards and staging chips: bytes → "410 KB" / "3.2 MB". */
export function formatFileSize(bytes: number): string {
  const KB = 1024;
  const MB = KB * 1024;
  if (bytes >= MB) return `${(bytes / MB).toFixed(1)} MB`;
  if (bytes >= KB) return `${Math.round(bytes / KB)} KB`;
  return `${bytes} B`;
}

/** Whether an attachment renders as an image preview card (mime sniffed by the browser on upload). */
export function isImageAttachment(mimeType: string | null | undefined): boolean {
  return typeof mimeType === "string" && mimeType.startsWith("image/");
}

/** List-row preview: tombstone text, raw system text, or "Sender: body/attachment". */
export function conversationPreviewText(
  conversation: ChatConversation,
  deletedText: string,
  attachmentText: string,
): string {
  const last = conversation.lastMessage;
  if (!last) return "";
  if (last.deleted) return deletedText;
  if (last.kind === "system") return last.text;
  const body = last.text || (last.hasAttachments ? attachmentText : "");
  return `${last.senderName}: ${body}`;
}
